import { z } from 'zod';

// Device schema for device information (used across entities)
// Note: Uses .nullable() to match database schema, but transforms null to undefined for type safety
export const DeviceSchema = z.object({
    id: z.string(),
    customerId: z.string().optional(),
    browser: z.string().nullable().transform(val => val ?? undefined).optional(),
    browserVersion: z.string().nullable().transform(val => val ?? undefined).optional(),
    os: z.string().nullable().transform(val => val ?? undefined).optional(),
    osVersion: z.string().nullable().transform(val => val ?? undefined).optional(),
    userAgent: z.string().nullable().transform(val => val ?? undefined).optional(),
    deviceType: z.string().nullable().transform(val => val ?? undefined).optional(),
    deviceModel: z.string().nullable().transform(val => val ?? undefined).optional(),
    screenWidth: z.number().nullable().transform(val => val ?? undefined).optional(),
    screenHeight: z.number().nullable().transform(val => val ?? undefined).optional(),
    timeZone: z.string().nullable().transform(val => val ?? undefined).optional(),
    browserLocale: z.string().nullable().transform(val => val ?? undefined).optional(),
    queryLocale: z.string().nullable().transform(val => val ?? undefined).optional(),
    queryCurrency: z.string().nullable().transform(val => val ?? undefined).optional(),
    // UTM parameters
    utmSource: z.string().nullable().transform(val => val ?? undefined).optional(),
    utmMedium: z.string().nullable().transform(val => val ?? undefined).optional(),
    utmCampaign: z.string().nullable().transform(val => val ?? undefined).optional(),
    // Google Ads
    gclid: z.string().nullable().transform(val => val ?? undefined).optional(),
    // Geo data
    geoIpAddress: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCity: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCityGeonameId: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoRegion: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoRegionIsoCode: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoRegionGeonameId: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoPostalCode: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCountry: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCountryCode: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCountryGeonameId: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoCountryIsEu: z.boolean().nullable().transform(val => val ?? undefined).optional(),
    geoContinent: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoContinentCode: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoContinentGeonameId: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoLatitude: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoLongitude: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoIsVpn: z.boolean().nullable().transform(val => val ?? undefined).optional(),
    // Timezone
    geoTimezoneName: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoTimezoneAbbreviation: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoTimezoneGmtOffset: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoTimezoneCurrentTime: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoTimezoneIsDst: z.boolean().nullable().transform(val => val ?? undefined).optional(),
    // Currency
    geoCurrencyName: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoCurrencyCode: z.string().nullable().transform(val => val ?? undefined).optional(),
    // Connection
    geoAsn: z.number().nullable().transform(val => val ?? undefined).optional(),
    geoAso: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoConnectionType: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoIspName: z.string().nullable().transform(val => val ?? undefined).optional(),
    geoOrgName: z.string().nullable().transform(val => val ?? undefined).optional(),
    // Timestamps
    createdAt: z.preprocess(
        (val) => val === null ? undefined : val,
        z.coerce.date().optional()
    ),
    updatedAt: z.preprocess(
        (val) => val === null ? undefined : val,
        z.coerce.date().optional()
    ),
    lastSeenAt: z.preprocess(
        (val) => val === null ? undefined : val,
        z.coerce.date().optional()
    ),
});

// Price schema for subscription pricing (used across entities)
export const PriceSchema = z.object({
    id: z.string(),
    accountId: z.string().optional(),
    productId: z.string().optional(),
    variantId: z.string().optional(),
    default: z.boolean().default(false),
    currencyOptions: z.any(),
    recurring: z.boolean().default(false),
    billingTiming: z.string().optional(),
    interval: z.string().nullable(),
    intervalCount: z.number().default(1),
    rebillWithOrder: z.boolean().default(false),
    rebillMode: z.string().nullable().optional(),
    rebillStepIntervalMs: z.number().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

// Subscription schema for subscriptions (used across entities)
export const SubscriptionSchema = z.object({
    id: z.string(),
    productId: z.string().optional(),
    priceId: z.string().optional(),
    orderId: z.string().optional(),
    quantity: z.number().default(1),
    customerId: z.string().optional(),
    defaultPaymentInstrumentId: z.string().nullable().optional(),
    preferredProcessorId: z.string().nullable().optional(),
    billingCycleAnchor: z.coerce.date().optional(),
    status: z.string(),
    cancelAt: z.coerce.date().nullable().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
    canceledAt: z.coerce.date().nullable().optional(),
    endedAt: z.coerce.date().nullable().optional(),
    currentPeriodEnd: z.coerce.date(),
    currentPeriodStart: z.coerce.date(),
    nextBillingDate: z.coerce.date(),
    trialEnd: z.coerce.date().nullable().optional(),
    trialStart: z.coerce.date().nullable().optional(),
    metadata: z.any().optional(),
    draft: z.boolean().default(false),
    storeId: z.string().optional(),
    accountId: z.string().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    price: PriceSchema.optional(),
    customer: z.object({
        id: z.string(),
        email: z.string().nullable(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
    }).optional(),
});

// Export types
export type Device = z.infer<typeof DeviceSchema>;
export type Price = z.infer<typeof PriceSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
